import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@/translations';
import { Alert, Form, Input, Modal, Select, Spin, Switch } from 'antd';
import { Company, CompanyType, Country, Select as SelectType } from '@/types';

type CompanyPopupProps = {
  data?: Company;
  countries: Country[];
  companyTypes: CompanyType[];
  customers: SelectType<number>[];
  onSubmit(company: Company): Promise<string | undefined>;
  onCancel(): void;
};

export const CompanyPopup: FC<CompanyPopupProps> = ({
  data,
  countries,
  companyTypes,
  customers,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<Company>();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const isAdd = data && !data.companyId;

  useEffect(() => {
    setErrorMessage(undefined);
    form.resetFields();
    if (data) form.setFieldsValue(data);
  }, [data, form]);

  const handleSubmit = useCallback(
    async (customer: Company) => {
      setErrorMessage(undefined);
      setLoading(true);
      const error = await onSubmit(customer);
      setErrorMessage(error);

      setLoading(false);
      if (!error) onCancel();
    },
    [onCancel, onSubmit],
  );

  return (
    <Modal
      open={!!data}
      onCancel={onCancel}
      title={`${t(isAdd ? 'create' : 'edit')} Company`}
      maskClosable
      closable={false}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={680}
      className="mw-100"
      forceRender
    >
      <Spin spinning={loading}>
        <Form
          id="company-form"
          form={form}
          onFinish={handleSubmit}
          size="large"
          labelCol={{ span: 6 }}
          autoComplete="off"
        >
          <Form.Item<Company> name="companyId" hidden>
            <Input />
          </Form.Item>

          {isAdd ? (
            <Form.Item<Company>
              label={t('customer')}
              name="customerId"
              required
              rules={[{ required: true }]}
            >
              <Select showSearch options={customers} optionFilterProp="label" />
            </Form.Item>
          ) : null}

          <Form.Item<Company>
            label="Company Code"
            name="companyCode"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={10} />
          </Form.Item>

          <Form.Item<Company>
            label="Company Name"
            name="companyName"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={100} />
          </Form.Item>

          <Form.Item<Company> label={t('country')} name="countryId">
            <Select
              showSearch
              options={countries}
              fieldNames={{ label: 'name', value: 'id' }}
              optionFilterProp="name"
            />
          </Form.Item>

          <Form.Item<Company> label="Company Type" name="companyTypeId">
            <Select
              showSearch
              options={companyTypes}
              fieldNames={{ label: 'name', value: 'id' }}
              optionFilterProp="name"
            />
          </Form.Item>

          <Form.Item<Company>
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
